/** Login state (spec §8.3): anonymous, password-only (pre_auth) or logged in, from GET /me/. */
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { api, unwrap } from './api/client'
import type { components } from './api/schema'
import { clearThemeMirror } from './theme'

export type Me = components['schemas']['MeResponse']
export type NextStep = NonNullable<components['schemas']['NextStepEnum']>

export type Session =
  | { state: 'anonymous' }
  | { state: 'pre_auth'; nextStep: NextStep; totpEnrolled: boolean }
  | { state: 'ready'; me: Me }

export const SESSION_KEY = ['session'] as const

let lastMe: Me | null = null

export async function fetchSession(previous?: Session): Promise<Session> {
  const result = await api.GET('/api/v1/me/')
  if (result.response.status === 401) {
    // A session that expired while the page was open stays "ready" here: the 401 opens the sign-in dialog
    // over the page (spec §10.3), instead of routing to Login and losing the user's unsaved work.
    if (previous?.state === 'ready') return previous
    lastMe = null
    return { state: 'anonymous' }
  }
  const data = unwrap(result)
  if ('id' in data) {
    lastMe = data
    return { state: 'ready', me: data }
  }
  return { state: 'pre_auth', nextStep: data.next_step, totpEnrolled: data.totp_enrolled }
}

export function useSession() {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: () => fetchSession(queryClient.getQueryData<Session>(SESSION_KEY)),
    staleTime: 60_000,
  })
}

/** The logged-in user; only call under a guard that has established a ready session. While logging out the
 * shell can render once more after the session is cleared, so the last loaded user is returned then. */
export function useMe(): Me {
  const { data } = useSession()
  if (data?.state === 'ready') return data.me
  if (!lastMe) throw new Error('useMe() outside a logged-in route')
  return lastMe
}

const RETURN_KEY = 'crm.return_to'

/** Remember the page a signed-out visitor asked for, so the end of the login flow lands there. */
export function rememberReturnPath(path: string | undefined): void {
  try {
    // Only same-site paths: never //host or /\host, which a browser treats as another site.
    const local = !!path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
    if (local && !path.startsWith('/login')) sessionStorage.setItem(RETURN_KEY, path)
    else sessionStorage.removeItem(RETURN_KEY)
  } catch {
    // Storage blocked: the flow ends on Home instead.
  }
}

export function returnPath(): string {
  try {
    return sessionStorage.getItem(RETURN_KEY) || '/'
  } catch {
    return '/'
  }
}

/** Re-read /me/ after a login step; the route guards then move the user on. */
export async function refreshSession(queryClient: QueryClient): Promise<Session> {
  return queryClient.fetchQuery({ queryKey: SESSION_KEY, queryFn: () => fetchSession(), staleTime: 0 })
}

const STEP_PATHS: Record<NextStep, string> = {
  totp: '/login/2fa',
  totp_enrolment: '/enrol-2fa',
  password_change: '/change-password',
}

export function stepPath(step: NextStep): string {
  return STEP_PATHS[step]
}

/** Drop everything this browser knows about the user (logout, 401, deactivated). */
export function clearClientState(queryClient: QueryClient): void {
  queryClient.clear()
  clearThemeMirror()
}

export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  return useCallback(async () => {
    await api.POST('/api/v1/auth/logout/')
    clearClientState(queryClient)
    queryClient.setQueryData<Session>(SESSION_KEY, { state: 'anonymous' })
    navigate('/login', { replace: true })
  }, [navigate, queryClient])
}
